import React, { useEffect, useState } from 'react';

import { addPhone } from 'src/components/Phones/Phones';
import { Banner, Button, Modal, Accordion, AccordionItem, Picker, Checkbox, TextField, Typography } from '@riac/design-system';
import { PhoneVerify } from 'src/components/Phones/PhoneVerify/PhoneVerify';
import { IAuctionItem } from 'src/pages/CatalogPage/CatalogPage.i';
import { IPhone } from 'src/components/Phones/Phones.i';
import { useFetch } from 'src/lib/FetchContext';
import { OverlayContainer } from '@react-aria/overlays';
import { Item } from '@react-stately/collections';
import { EmptyState } from 'src/components/EmptyState';
import { BirthdayVerifier } from 'src/components/AgeVerificationCheck/BirthdayVerifier';


interface ITextAlertModal {
  item: IAuctionItem;
  closeModal(): void;
  addAlert(): void;
}

export const TextAlertModal = (props: ITextAlertModal) => {
  const [error, setError] = useState('');
  const [phoneList, setPhoneList] = useState<IPhone[]>([]);
  const [selectedPhone, setSelectedPhone] = useState('0');
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [shouldConfirmOver21, setShouldConfirmOver21] = useState(false);
  const [confirmedOver21, setConfirmedOver21] = useState(false);
  // simple counter to watch--triggers refetch of phone list when incremented after a verification status change
  const [updatePhoneList, setUpdatePhoneList] = useState(1);
  const { fetch } = useFetch();

  useEffect(() => {
    const getPhones = async () => {
      const phoneReq = await fetch('/api/account/phone/getPhones', {
        credentials: 'include',
      });
      const phoneRes: IPhone[] = await phoneReq.json();
      if(Array.isArray(phoneRes)) {
        const cellPhones = phoneRes.filter((ph) => ph.PhoneTypeID === 2);
        cellPhones.sort((a, b) => {
          if(a.VerifiedID === 1 && b.VerifiedID !== 1) {
            return -1;
          } else if(a.VerifiedID !== 1 && b.VerifiedID === 1) {
            return 1;
          } else {
            return 0;
          }
        });
        setPhoneList(cellPhones);
        if(selectedPhone === '0') setSelectedPhone(cellPhones[0]?.PhoneID?.toString() || '0');
      }
    };
    const getOver18 = async () => {
      const request = await fetch('/api/account/over21', {
        credentials: 'include',
      });
      const response = await request.json();
      if(response[0].ConfirmedOver21) {
        setConfirmedOver21(true);
      } else {
        setConfirmedOver21(false);
        setShouldConfirmOver21(true);
      }
    };
    getPhones();
    getOver18();
  }, [updatePhoneList]);

  function updateTextAlertPhone(e) {
    setSelectedPhone(e.value);
    setError('');
  }

  async function handleSubmit() {
    let num = newPhoneNumber.trim();
    if(num.indexOf('+') !== 0) {
      // US-based phone number. Perform checks and formatting
      num = num.replace(/[^0-9.]/g, '');
      if(num.indexOf('911') === 0 || num.length !== 10) {
        setError(
          'To be eligible for text alerts, the phone number entered must be 10 digits and '
            + 'may not begin with the digits 911. Please edit your number and try again. If you are using a phone number'
            + ' registered outside the United States, please enter your country code along with your 10 digit number.'
            + ' Example: +22 607 123 4567'
        );
        return;
      }
      // prettify number before saving into phonebook
      num = `(${num.substring(0, 3)})${num.substring(3, 6)}-${num.substring(6, 10)}`;
    }
    const newPhoneID = await addPhone(2, num);
    const newPhone: IPhone = {
      PhoneID: newPhoneID,
      AccountID: 0,
      PhoneTypeID: 2,
      Phone: num,
      VerifiedID: 0,
      PhoneType: 'Cell',
    };
    setError('');
    setSelectedPhone(newPhoneID.toString());
    setPhoneList((oldList) => [...oldList, newPhone]);
  }

  async function submitTextAlert() {
    if(!selectedPhone || parseInt(selectedPhone) < 1) {
      setError('Please select a phone number');
      return;
    }
    const selectedNumber = phoneList
      .filter((phone) => phone.PhoneID.toString() == selectedPhone)[0]
      ?.Phone?.trim();
    if(!selectedNumber) {
      setError('Please select a phone number');
      return;
    }
    let formattedNumber = '';
    if(selectedNumber.indexOf('+') === 0) {
      formattedNumber = `+${selectedNumber.replace(/[^0-9.]/g, '')}`;
    } else {
      formattedNumber = `+1${selectedNumber.replace(/[^0-9.]/g, '')}`;
    }
    await fetch('/api/account/textalerts/put', {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fphone: formattedNumber,
        auctid: props.item.AuctionID,
        lotnumber: props.item.LotNumber,
      }),
    });
    props.addAlert();
    props.closeModal();
  }

  const selectedPhoneObj = phoneList.find((ph) => ph.PhoneID.toString() === selectedPhone);
  const verified = selectedPhoneObj?.VerifiedID || 0;

  const defaultOption = { value: '0', label: 'Select a Phone Number' };
  const addNewOption = { value: '-1', label: 'Add a New Phone' };
  const phoneOptions = phoneList.map((phone) => ({
    key: phone.PhoneID,
    value: phone.PhoneID.toString(),
    label: `(Cell) ${phone.Phone} ${
      phone.VerifiedID == 1 ? String.fromCharCode(10003) + ' Verified' : ''
    } ${phone.VerifiedID == 2 ? ' --Please complete verification--' : ''}`,
  }));

  const allSelectOptions = [defaultOption, ...phoneOptions, addNewOption];
  const selectedOptionKey = allSelectOptions.findIndex((opt) => opt.value == selectedPhone) + '';

  const hasSelectedPhone = parseInt(selectedPhone) > 0;
  const isVerifyingPhone = hasSelectedPhone &&
    verified === 0 || verified === 2;
  const readyToConsent = verified === 1 || (verified === 0 && confirmedOver21);

  return (
    <OverlayContainer>
      <Modal
        isOpen
        onClose={props.closeModal}
        showCloseButton
        isDismissable
        title='New Text Reminder'
        dialogClassName='min-h-[420px]'
        footerButtons={isVerifyingPhone ? undefined : <>
          {selectedPhone === '-1' ? (
            <Button
              isDisabled={!newPhoneNumber}
              onPress={handleSubmit}
            >
              Save Phone
            </Button>
          ) : readyToConsent ? (
            phoneList.length > 0 && <Button
              isDisabled={!confirmedOver21}
              onPress={submitTextAlert}
            >
              Save Text Alert
            </Button> || <Button variant='secondary' onPress={props.closeModal}>Close</Button>
          ) : ''}
        </>}
      >
        {phoneList.length === 0 &&(
          <EmptyState
            title='No Cell Phones Found'
            description='You must have a cell phone number registered to your account to set up text alerts.'
          >
            <Button to='/account/phone-book'>
              Go to Account Phone Book
            </Button>
          </EmptyState>
        )}
        <div className='flex flex-col gap-3'>
          {phoneList.length !== 0 && (
            <Picker
              label = 'Choose a Phone to Receive Text Messages:'
              selectedKey = {selectedOptionKey}
              onSelectionChange = {(val) => updateTextAlertPhone(allSelectOptions[val])}
            >
              {allSelectOptions.map((phone, index) => (
                <Item key = {index}>{phone.label}</Item>
              ))}
            </Picker>
          )}
          {selectedPhone === '-1' && (
            <div className='flex flex-col gap-3'>
              <Typography>
                If you are using a phone number registered outside the United States, please enter your
                country code along with your 10 digit number.
              </Typography>
              <Typography>
                Example: +22 607 123 4567
              </Typography>
              <TextField
                label='Cell Phone #'
                value={newPhoneNumber}
                onChange={(e) => setNewPhoneNumber(e)}
                description='*Only numbers registered to a mobile device are eligible to receive text alert
                notifications. Standard Message & Data rates may apply.'
              />
              {error && <Banner color='error' size='sm' children={error} />}
            </div>
          )}
          {parseInt(selectedPhone) > 0 ? (
            verified === 0 || verified === 2 ? (
              <>
                <PhoneVerify
                  updatePhoneList={() => setUpdatePhoneList((prev) => prev + 1)}
                  phone={selectedPhoneObj}
                  key={selectedPhone}
                />
                <div className='h-2'/>
              </>
            ) : (
              <>
                <Typography>
                  I consent to Rock Island Auction Company sending text message alerts to my phone as a
                  reminder that the lot I want to bid upon is coming up for auction. Message and Data
                  Rates May Apply.
                </Typography>
                {error && <Banner color='error' size='sm' children={error} />}
                {shouldConfirmOver21
                  && <BirthdayVerifier
                    showButtons={false}
                    validationYears={21}
                    onValidDate={() => {
                      setConfirmedOver21(true);
                    }}
                  />
                }
                <Accordion>
                  <AccordionItem
                    heading='How do Text Reminders Work?'
                    buttonClassName='text-[22px] leading-[22px]'
                    children={
                      <div className='flex flex-col gap-2'>
                        <Typography>
                          A text message notification is sent 20 lots prior to your lot during our live
                          auction directly to your phone to remind you that your lot is coming up soon.
                        </Typography>
                        <Typography heading size='sm' color='text-light' weight='medium'>Please Note:</Typography>
                        <Typography>
                          If you request a Text Message Alert on lot 1-20 you will only receive one
                          reminder for all of those lots at the beginning of our auction that the
                          auction is beginning.
                        </Typography>
                        <Typography>
                          Text Alerts within 20 lots of each other will not be sent in an attempt to
                          reduce spam. For example, if you know you already have a text alert set for
                          lot 30 and one set for lot 49 a text alert will be sent to you only for lot
                          30. If you have an alert set for lots 50, 51, 52, and 53 you will only
                          receive an alert for lot 50.
                        </Typography>
                        <Typography>
                          Message and Data Rates May Apply.
                        </Typography>
                      </div>
                    }
                  />
                </Accordion>
              </>
            )
          ) : null}
        </div>
      </Modal>
    </OverlayContainer>
  );
};
